import { Fragment, useMemo } from 'react';

import { InfoOutlined } from '@mui/icons-material';
import { Box, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { getIn, useFormikContext } from 'formik';

import { CissNumberField } from 'components/fields';
import { SubTitle } from 'components/text';
import { DreFormGroup } from 'pages/financeiro/dre/components/templates/DreFormGroup';
import { DreFormRow } from 'pages/financeiro/dre/components/templates/DreFormRow';
import { useRecalculateDre } from 'pages/financeiro/dre/hooks';
import { DreDespesasConfiguracao, DreForm } from 'pages/financeiro/dre/types';
import { getLines } from 'pages/financeiro/dre/utils';
import { brlPrice } from 'util/format';

export function DreFormDespesas() {
    const { errors, values, setFieldValue } = useFormikContext<DreForm>();
    const { recalculateValidatedDre } = useRecalculateDre();

    return useMemo(
        () => (
            <DreFormGroup label="Despesas" id="3">
                {values.dreMercadologicaDespesas.map((despesas, index) => (
                    <Fragment key={despesas.dreMercadologicaGrupoExibicao.idDreMercadologicaGrupoExibicao}>
                        <SubTitle
                            sx={{ m: 0 }}
                            id={`3_${despesas.dreMercadologicaGrupoExibicao.idDreMercadologicaGrupoExibicao}`}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <strong>{despesas.dreMercadologicaGrupoExibicao.dsDreMercadologicaGrupoExibicao}</strong>
                                    <span>{brlPrice(despesas.dreMercadologicaGrupoExibicao.vlTotal)}</span>
                                </Box>
                            }
                        />

                        {Object.entries(getLines<DreDespesasConfiguracao>(despesas.dreMercadologicaValor, 'dreMercadologicaConfigDespesa')).map(
                            ([line, configuracoes]) => {
                                return (
                                    <DreFormRow key={line}>
                                        {configuracoes.map((config) => {
                                            const preffix = `dreMercadologicaDespesas[${index}].dreMercadologicaValor[${config.index}]`;
                                            const isFormula = config.dreMercadologicaConfigDespesa.dreTipoPreenchimento.idDreTipoPreenchimento === 1;
                                            const tooltip = config.dreMercadologicaConfigDespesa.dsObservacao;

                                            return (
                                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }} key={preffix}>
                                                    <CissNumberField
                                                        allowNegative
                                                        inputType="decimal"
                                                        disabled={isFormula}
                                                        value={config.vlTotal}
                                                        name={`${preffix}.vlTotal`}
                                                        onBlur={recalculateValidatedDre}
                                                        error={getIn(errors, `${preffix}.vlTotal`)}
                                                        label={`${config.dreMercadologicaConfigDespesa.dsNomeExibicao} (R$)`}
                                                        onChange={(e) => setFieldValue(`${preffix}.vlTotal`, Number(e.target.value))}
                                                    />

                                                    {tooltip && (
                                                        <Tooltip title={tooltip}>
                                                            <InfoOutlined />
                                                        </Tooltip>
                                                    )}

                                                    <FormControlLabel
                                                        label="Recorrente"
                                                        disabled={isFormula}
                                                        control={<Checkbox />}
                                                        checked={config.fgRecorrente}
                                                        name={`${preffix}.fgRecorrente`}
                                                        onChange={(e, checked) => setFieldValue(`${preffix}.fgRecorrente`, checked)}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </DreFormRow>
                                );
                            },
                        )}
                    </Fragment>
                ))}
            </DreFormGroup>
        ),
        [values.dreMercadologicaDespesas, errors.dreMercadologicaDespesas],
    );
}
